import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useState, useRef } from "react";
import { 
  Search, 
  PenTool, 
  Target, 
  Shield, 
  Heart, 
  Zap, 
  ArrowRight,
  CheckCircle,
  TrendingUp,
  Users,
  Lightbulb,
  BarChart3
} from "lucide-react";

const arrows = {
  research: [
    { label: "Ground Research", icon: Search, description: "Comprehensive data collection and analysis" },
    { label: "Agenda Setting & Inputs", icon: Lightbulb, description: "Strategic planning and stakeholder input" },
    { label: "Policy Discussion", icon: Users, description: "Collaborative policy development sessions" },
  ],
  implement: [
    { label: "Policy Adoption", icon: CheckCircle, description: "Official policy approval and implementation" },
    { label: "Monitoring & Evaluation", icon: BarChart3, description: "Continuous assessment and tracking" },
    { label: "Implementation", icon: Target, description: "Strategic execution and deployment" },
    { label: "Policy Maintenance", icon: Shield, description: "Ongoing support and updates" },
  ],
};

const stages = {
  "Policy Formation": "Central process where gathered insights are designed into actionable policies with stakeholder collaboration and strategic planning.",
  "Impact Together Lens": "A unique approach ensuring every policy is seen through a people-centric, tech-integrated lens for maximum social impact and community benefit.",
  "Policy Evaluation": "Comprehensive analysis of policy effectiveness, measuring outcomes and identifying areas for improvement with data-driven insights.",
  "Social Impact": "Final measurable outcome benefiting communities and ecosystems with sustainable long-term results and positive change.",
  "Ground Research": "Comprehensive data collection and analysis to understand current challenges and opportunities.",
  "Agenda Setting & Inputs": "Strategic planning and stakeholder input to define clear objectives and priorities.",
  "Policy Discussion": "Collaborative policy development sessions with key stakeholders and experts.",
  "Policy Adoption": "Official policy approval and implementation with proper governance and oversight.",
  "Monitoring & Evaluation": "Continuous assessment and tracking of policy performance and outcomes.",
  "Implementation": "Strategic execution and deployment of policies across relevant sectors.",
  "Policy Maintenance": "Ongoing support and updates to ensure long-term effectiveness and relevance.",
};

const PolicyFlow = () => {
  const { ref, inView } = useInView({ triggerOnce: true });
  const [hovered, setHovered] = useState(null);
  const [activeStage, setActiveStage] = useState(null);
  const tooltipRef = useRef(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    },
  };

  const tooltipVariants = {
    hidden: { opacity: 0, scale: 0.8, y: 10 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25
      }
    },
  };

  return (
    <section
      ref={ref}
      className="bg-gradient-to-br from-primary/10 to-secondary/10 py-8 px-4 sm:px-10 text-center font-body relative overflow-hidden overflow-x-hidden w-full"
    >
      {/* Background decorative elements (optional, can be removed if too much) */}
      {/* <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-secondary/10 to-primary/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
      </div> */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        className="relative z-10"
      >
        <motion.h2
          variants={itemVariants}
          className="text-4xl font-heading font-bold text-primary mb-6"
        >
          Our Policy Offering
        </motion.h2>

        <motion.p
          variants={itemVariants}
          className="text-lg text-gray-600 max-w-3xl mx-auto mb-12 leading-relaxed font-body"
        >
          A comprehensive approach to policy development that combines research, design, and implementation with cutting-edge technology and human-centered design principles
        </motion.p>

        {/* Main stages */}
        <motion.div
          variants={itemVariants}
          className="flex flex-col md:flex-row justify-center items-center gap-6 md:gap-16 mb-16"
        >
          {[
            { name: "Research", icon: Search, color: "from-primary to-secondary", bgColor: "bg-secondary" },
            { name: "Design", icon: PenTool, color: "from-primary to-secondary", bgColor: "bg-primary" },
            { name: "Implement", icon: Target, color: "from-secondary to-primary", bgColor: "bg-secondary" },
          ].map((stage, idx) => (
            <motion.div
              key={stage.name}
              whileHover={{ scale: 1.05, y: -8 }}
              whileTap={{ scale: 0.95 }}
              className="flex flex-col items-center group cursor-pointer relative"
              onClick={() => setActiveStage(activeStage === stage.name ? null : stage.name)}
            >
              {/* Circle label (1, 2, 3) */}
              <div className="mb-3">
                <div className="w-8 h-8 rounded-full bg-secondary text-white font-semibold flex items-center justify-center shadow">
                  {idx + 1}
                </div>
              </div>

              <motion.div 
                className={`w-20 h-20 rounded-3xl bg-gradient-to-br ${stage.color} flex items-center justify-center text-white mb-6 shadow-xl group-hover:shadow-2xl transition-all duration-500 relative overflow-hidden`}
                whileHover={{ rotate: 5 }}
              >
                <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                <stage.icon className="w-8 h-8 relative z-10" />
              </motion.div>

              <span className="text-xl font-heading font-semibold text-gray-800 group-hover:text-primary transition-colors">
                {stage.name}
              </span>
            </motion.div>
          ))}
        </motion.div>

        {/* Research Phase */}
        <motion.div
          variants={itemVariants}
          className="mb-16"
        >
          <h3 className="text-3xl font-heading font-bold text-gray-900 mb-10">Research Phase</h3>
          <div className="flex flex-wrap justify-center gap-6">
            {arrows.research.map((item, idx) => (
              <motion.div
                key={idx}
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.98 }}
                className="bg-white border border-gray-200 px-8 py-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group relative"
                onMouseEnter={() => setHovered(item.label)}
                onMouseLeave={() => setHovered(null)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-secondary text-white group-hover:scale-110 transition-transform duration-300">
                    <item.icon className="w-6 h-6" />
                  </div>
                  <span className="font-heading font-semibold text-gray-800 group-hover:text-primary text-lg">
                    {item.label}
                  </span>
                </div>
                {/* Tooltip */}
                <AnimatePresence>
                  {hovered === item.label && (
                    <motion.div
                      ref={tooltipRef}
                      variants={tooltipVariants}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      className="absolute -top-16 left-1/2 transform -translate-x-1/2 z-50"
                    >
                      <div className="bg-gray-900 text-white px-4 py-3 rounded-xl shadow-2xl max-w-xs text-sm leading-relaxed font-body">
                        <p>{stages[item.label] || item.description}</p>
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Policy Formation Center */}
        <motion.div
          variants={itemVariants}
          className="mb-16"
        >
          <motion.div
            whileHover={{ scale: 1.02, y: -3 }}
            whileTap={{ scale: 0.98 }}
            onMouseEnter={() => setHovered("Policy Formation")}
            onMouseLeave={() => setHovered(null)}
            className="inline-block relative group cursor-pointer"
          >
            <div className="bg-gradient-to-br from-primary to-secondary text-white px-10 py-8 rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-500 relative overflow-hidden">
              <div className="absolute inset-0 bg-white/10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 rounded-2xl bg-white/20 backdrop-blur-sm">
                    <PenTool className="w-8 h-8" />
                  </div>
                  <h3 className="text-3xl font-heading font-bold">Policy Formation</h3>
                </div>
                <p className="text-white/90 text-lg leading-relaxed font-body">Central process where insights become actionable policies</p>
              </div>
            </div>
            
            {/* Impact Together Lens Badge */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.8, type: "spring", stiffness: 200 }}
              className="absolute -bottom-6 left-1/2 transform -translate-x-1/2"
            >
              <div className="bg-gradient-to-r from-secondary to-blue-500 text-white px-6 py-3 text-sm font-heading font-semibold rounded-full shadow-xl flex items-center gap-3 backdrop-blur-sm">
                <Zap className="w-4 h-4" />
                Impact Together Lens
              </div>
            </motion.div>

            {/* Tooltip */}
            <AnimatePresence>
              {hovered === "Policy Formation" && (
                <motion.div
                  variants={tooltipVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  className="absolute -top-20 left-1/2 transform -translate-x-1/2 z-50"
                >
                  <div className="bg-gray-900 text-white px-6 py-4 rounded-xl shadow-2xl max-w-md text-sm leading-relaxed font-body">
                    <p>{stages["Policy Formation"]}</p>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>

        {/* Implementation Phase */}
        <motion.div
          variants={itemVariants}
          className="mb-16"
        >
          <h3 className="text-3xl font-heading font-bold text-gray-900 mb-10">Implementation Phase</h3>
          <div className="flex flex-wrap justify-center gap-6">
            {arrows.implement.map((item, idx) => (
              <motion.div
                key={idx}
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.98 }}
                className="bg-white border border-gray-200 px-8 py-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group relative"
                onMouseEnter={() => setHovered(item.label)}
                onMouseLeave={() => setHovered(null)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-secondary text-white group-hover:scale-110 transition-transform duration-300">
                    <item.icon className="w-6 h-6" />
                  </div>
                  <span className="font-heading font-semibold text-gray-800 group-hover:text-primary text-lg">
                    {item.label}
                  </span>
                </div>

                {/* Tooltip */}
                <AnimatePresence>
                  {hovered === item.label && (
                    <motion.div
                      variants={tooltipVariants}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      className="absolute -top-16 left-1/2 transform -translate-x-1/2 z-50"
                    >
                      <div className="bg-gray-900 text-white px-4 py-3 rounded-xl shadow-2xl max-w-xs text-sm leading-relaxed font-body">
                        <p>{stages[item.label] || item.description}</p>
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Final Outcomes */}
        <motion.div
          variants={itemVariants}
          className="mb-12"
        >
          <div className="flex flex-wrap justify-center gap-8">
            {[
              { label: "Policy Evaluation", icon: TrendingUp, color: "from-secondary to-primary", bgColor: "bg-secondary" },
              { label: "Social Impact", icon: Heart, color: "from-primary to-secondary", bgColor: "bg-primary" },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                whileHover={{ scale: 1.05, y: -8 }}
                whileTap={{ scale: 0.95 }}
                className={`bg-gradient-to-br ${item.color} text-white px-10 py-8 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 cursor-pointer group relative overflow-visible`}
                onMouseEnter={() => setHovered(item.label)}
                onMouseLeave={() => setHovered(null)}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.2 }}
              >
                <div className="absolute inset-0 bg-white/10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700 pointer-events-none"></div>
                <div className="relative z-10">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 rounded-2xl bg-white/20 backdrop-blur-sm">
                      <item.icon className="w-8 h-8" />
                    </div>
                    <h3 className="text-2xl font-heading font-bold">{item.label}</h3>
                  </div>
                  <p className="text-white/90 text-lg leading-relaxed font-body">
                    {item.label === "Policy Evaluation" 
                      ? "Measuring effectiveness and outcomes with data-driven insights" 
                      : "Creating lasting positive change in communities"}
                  </p>
                </div>
                {/* Tooltip rendered above the hovered card only */}
                <AnimatePresence>
                  {hovered === item.label && (
                    <motion.div
                      variants={tooltipVariants}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      className="absolute left-1/2 -top-24 transform -translate-x-1/2 z-50"
                    >
                      <div className="bg-gray-900 text-white px-6 py-4 rounded-xl shadow-2xl max-w-md text-sm leading-relaxed font-body">
                        <p>{stages[item.label]}</p>
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default PolicyFlow;
